/** @jsx jsx */
// eslint-disable-next-line
import React, { Component } from 'react'
import { css, jsx } from '@emotion/core'
import { withStyles } from '@material-ui/core'
import MuiDialogContent from '@material-ui/core/DialogContent'
import PropTypes from 'prop-types'
import { theme } from '../../constants'
import { ProductNames } from '../../constants/payments'
import formatPrice from '../../utils/formatPrice'
import localizePriceByCountry from '../../utils/localizePriceByCountry'
import localizeCurrency from '../../utils/localizeCurrency'
import CheckBox from '@material-ui/core/Checkbox'
import logo from '../../assets/game-cloud-logo.png'

const styles = {}
styles.container = css`
  position: relative;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  border: 1px solid var(--game-cloud-purple);
  border-radius: 8px;
  padding: 16px;
  padding-right: 32px;
  margin-bottom: 16px;
  color: white;
  background: var(--game-cloud-purple-black-gradient);
`
styles.title = css`
  font-weight: bold;
  margin-bottom: 8px;
  margin-top: 0;
  font-size: 20px;
  margin-bottom: 30px;
`

styles.productLogo = {
  height: '40px',
  marginBottom: '10px'
}
styles.description = css``
styles.featuresContainer = css`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
`
styles.featureTitle = css`
  font-weight: bold;
`
styles.featureDescription = css``
styles.addonsContainer = css`
  display: flex;
  flex-direction: column;
`
styles.addonTitle = css`
  font-weight: bold;
  margin-bottom: 8px;
`
styles.addonDescription = css`
  margin-left: 12px;
`
styles.addonPrice = css`
  min-width: 120px;
  max-width: 120px;
  text-align: right;
  margin-right: 12px;
  font-weight: bold;
  font-size: 16px;
`
styles.addonContainer = css`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`
styles.addonToggleContainer = css`
  display: flex;
  align-items: center;
`
styles.totalContainer = css`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`
styles.removeButton = css`
  cursor: pointer;
  color: grey;
  :hover {
    text-decoration: underline;
  }
`

styles.productPrice = css`
  font-size: 20px;
  font-weight: bold;
`

styles.planQuantityContainer = css`
  position: absolute;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  top: 16px;
  right: 16px;
`
styles.packQuantityContainer = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`
styles.quantity = css`
  min-width: 16px;
  max-width: 16px;
  text-align: center;
  margin: 0px 12px;
  user-select: none;
`
styles.quantityButton = css`
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid ${theme.palette.common.charolBlack};
  background: var(--game-cloud-purple);
  cursor: pointer;
  user-select: none;
  font-size: 30px;
  font-weight: bold;
`
styles.plusMinusIcon = css`
  margin-top: -9px;
`

styles.disabled = css`
  pointer-events: none;
  opacity: 0.5;
`

const DialogContent = withStyles(() => ({
  root: {
    flex: 'unset !important',
    padding: '0px !important',
    '& .MuiSwitch-colorPrimary.Mui-checked.Mui-disabled + .MuiSwitch-track': {
      backgroundColor: theme.palette.primary.main,
      opacity: 0.5
    },
    '& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.palette.primary.main,
      opacity: 1
    },
    '& .MuiSwitch-thumb': {
      color: theme.palette.common.white
    }
  }
}))(MuiDialogContent)

function OneTimeProducts(props) {
  const [localizedCurrencyCode, setLocalizedCurrencyCode] = React.useState('EUR')
  const [total, setTotal] = React.useState(0)

  React.useEffect(() => {
    const newTotal = props.products.reduce((acc, v) => {
      return acc + localizePriceByCountry(v, props.currencyCode, props.countryCode) * v.quantity
    }, 0)

    setTotal(newTotal)
  }, [props.products, props.currencyCode])

  React.useEffect(() => {
    const firstLocalizedProduct =
      props.oneTimeProducts.find(v => v.localizedPrices.length > 0) || props.oneTimeProducts[0]
    if (firstLocalizedProduct) {
      const newLocalizedCurrencyCode = localizeCurrency(firstLocalizedProduct, props.countryCode)
      setLocalizedCurrencyCode(newLocalizedCurrencyCode)
    }
  }, [props.oneTimeProducts, props.countryCode])

  return (
    <DialogContent>
      <div css={styles.container}>
        <div>
          <img css={styles.productLogo} src={logo} alt="logo" />
          <p css={styles.title}>{window.I18n.t('products.minutesPack')}</p>
        </div>

        <div css={styles.addonsContainer}>
          {props.oneTimeProducts.map(v => {
            const product = props.products.find(p => p.id === v.id)
            const productInfo = ProductNames.find(p => p.value === v.priceCode)

            return (
              <div css={styles.addonContainer} key={`container-${v.id}`}>
                <div css={styles.addonToggleContainer}>
                  <CheckBox
                    style={{
                      padding: 0,
                      color: 'var(--game-cloud-purple)',
                      '&$checked': {
                        color: 'var(--game-cloud-purple)'
                      }
                    }}
                    name={v.id}
                    checked={product && product.quantity > 0}
                    onChange={() => {
                      if (product && product.quantity > 0) {
                        props.onUpdate([{ ...product, quantity: 0 }])
                      } else {
                        props.onUpdate([{ ...v, quantity: 1 }])
                      }
                    }}
                  />
                  <span css={styles.addonDescription}>
                    {window.I18n.t(`plans.${productInfo.translationKey}`)}
                  </span>
                </div>
                <div
                  css={css`
                    ${styles.packQuantityContainer};
                    ${!product || product.quantity <= 0 ? styles.disabled : ''}
                  `}
                >
                  <div
                    css={css`
                      ${styles.quantityButton}
                      ${product && product.quantity <= 1 ? styles.disabled : ''}
                    `}
                    onClick={() => {
                      props.onUpdate([{ ...product, quantity: Math.max(0, product.quantity - 1) }])
                    }}
                  >
                    <span css={styles.plusMinusIcon}>-</span>
                  </div>
                  <span css={styles.quantity}>{product ? product.quantity : 0}</span>
                  <div
                    css={css`
                      ${styles.quantityButton}
                      ${product && product.quantity >= 99 ? styles.disabled : ''}
                    `}
                    onClick={() => {
                      props.onUpdate([{ ...product, quantity: Math.min(99, product.quantity + 1) }])
                    }}
                  >
                    <span css={styles.plusMinusIcon}>+</span>
                  </div>

                  <span css={styles.addonPrice}>
                    {localizeCurrency(v, props.countryCode)}{' '}
                    {formatPrice(
                      localizePriceByCountry(v, props.currencyCode, props.countryCode),
                      props.currencyCode
                    )}
                  </span>
                </div>
              </div>
            )
          })}
        </div>

        <div css={styles.totalContainer}>
          {false && (
            <a
              css={styles.removeButton}
              onClick={() => props.onUpdate(props.products.map(v => [{ ...v, quantity: 0 }]))}
            >
              {window.I18n.t('products.remove')}
            </a>
          )}
          <span css={styles.totalDescription}>
            <span css={styles.productPrice}>
              {window.I18n.t('products.totalPack', {
                currency: localizedCurrencyCode,
                cost: formatPrice(total, localizedCurrencyCode)
              })}
            </span>
          </span>
        </div>
      </div>
    </DialogContent>
  )
}

OneTimeProducts.defaultProps = {
  countryCode: ''
}

OneTimeProducts.propTypes = {
  currencyCode: PropTypes.string.isRequired,
  countryCode: PropTypes.string,
  products: PropTypes.array.isRequired,
  oneTimeProducts: PropTypes.array.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export default OneTimeProducts
